import { v4 as uuidv4 } from 'uuid';
import getApiConfig from '../config/apiConfig';

// Helper function to transform PascalCase to camelCase
const transformToCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const transformed = {};
  Object.keys(obj).forEach(key => {
    let camelKey = key.charAt(0).toLowerCase() + key.slice(1);
    
    // Special handling for Id field - map to contactId for consistency
    if (key === 'Id' || key === 'id') {
      camelKey = 'contactId';
      transformed[camelKey] = obj[key];
      return;
    }
    
    transformed[camelKey] = obj[key];
  });
  return transformed;
};

// Contact JSON Schema
const createEmptyContact = () => ({
  contactId: '',
  firstName: '',
  lastName: '',
  email: '',
  title: '',
  mainPhone: '',
  secondaryPhone: '',
  street1: '',
  street2: '',
  city: '',
  province: '',
  country: '',
  postalCode: '',
  notes: '',
  linkedInProfile: '',
  createdBy: '',
  createdDate: ''
});

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }
  const data = await response.json();
  return Array.isArray(data) ? data.map(transformToCamelCase) : transformToCamelCase(data);
};

export const contactService = {
  // Create empty contact
  createEmptyContact,

  // Validate configuration (no-op for API-based services)
  validateConfiguration() {
    return true;
  },

  // Get all contacts
  async getContacts(pageSize = 10000) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/contacts?pageSize=${pageSize}&pageNumber=1`);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${errorText}`);
      }
      
      const data = await response.json();
      
      // Handle both array and paginated response formats
      const contacts = data.data || data;
      if (!Array.isArray(contacts)) {
        throw new Error('Invalid response format from contacts API');
      }
      
      // Transform all contacts to camelCase
      return contacts.map(contact => transformToCamelCase(contact));
    } catch (error) {
      console.error('Error in getContacts:', error);
      throw error;
    }
  },

  // Get contact by ID
  async getContact(contactId) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/contacts/${contactId}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${errorText}`);
      }
      
      const data = await response.json();
      return transformToCamelCase(data);
    } catch (error) {
      throw error;
    }
  },

  // Save contact (create or update)
  async saveContact(contact, currentUserEmail) {
    try {
      const { apiBaseUrl } = getApiConfig();
      
      // Check if this is a new contact or existing contact
      const isNew = !contact.contactId && !contact.id;
      
      // For new contacts, generate ID and set created info
      if (isNew) {
        contact.contactId = uuidv4();
        contact.createdBy = currentUserEmail;
        contact.createdDate = new Date().toISOString();
      }

      // Use the appropriate ID for the URL
      const contactId = contact.contactId || contact.id;
      
      const method = isNew ? 'POST' : 'PUT';
      const url = isNew 
        ? `${apiBaseUrl}/contacts`
        : `${apiBaseUrl}/contacts/${contactId}`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contact)
      });

      return handleResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Delete contact
  async deleteContact(contactId) {
    try {
      if (!contactId) {
        throw new Error('Contact ID is required for deletion');
      }

      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/contacts/${contactId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete contact');
      }

      return response.json();
    } catch (error) {
      console.error('Delete contact error:', error);
      throw error;
    }
  },

  // Get contacts by company (or without company if companyId is null)
  async getContactsByCompany(companyId = null, opportunityId = null) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const url = `${apiBaseUrl}/contacts/company/${companyId || 'null'}${opportunityId ? `?opportunityId=${opportunityId}` : ''}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${errorText}`);
      }
      
      const data = await response.json();
      return Array.isArray(data) ? data.map(contact => transformToCamelCase(contact)) : [];
    } catch (error) {
      console.error('Error getting contacts by company:', error);
      throw error;
    }
  }
}; 